import React, { createContext, useContext, ReactNode, useState, useEffect } from "react";
import { AccountBalance, PaymentLimit, User, ToggleSwitch, v4Service } from "src/api";
import { useUrlParamsContext } from "src/contexts";

interface DepositWithdrawContextType {
  user: User;
  paymentsLimits: PaymentLimit;
  balance: AccountBalance;
  widgetDataLoading: boolean;
  widgetDataError: string;
}

const initialPaymentLimits: PaymentLimit = {
  currency_id: "",
  deposit_min_amount: 0,
  withdrawal_min_amount: 0,
};

const initialBalance: AccountBalance = {
  currency: {
    currency_id: "",
    precision: 2,
  },
  free_balance: 0,
  wallet_id: "",
};

const initialUser: User = {
  user_id: "",
  limit_group_id: "",
  mfa_status: ToggleSwitch.Off,
};

const DepositWithdrawContext = createContext<DepositWithdrawContextType | undefined>(undefined);

interface DepositWithdrawProviderProps {
  children: ReactNode;
}

export const DepositWithdrawProvider: React.FC<DepositWithdrawProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User>(initialUser);
  const [paymentsLimits, setPaymentsLimits] = useState<PaymentLimit>(initialPaymentLimits);
  const [balance, setBalance] = useState<AccountBalance>(initialBalance);
  const [widgetDataLoading, setWidgetDataLoading] = useState<boolean>(true);
  const [widgetDataError, setWidgetDataError] = useState<string>("");
  const { currencyId, walletId } = useUrlParamsContext();

  useEffect(() => {
    const fetchWidgetData = async () => {
      try {
        const user = await v4Service.getUser({});

        const paymentsLimits = await v4Service.getPaymentsLimits({
          currency_id: currencyId,
          limit_group_id: user.limit_group_id,
        });

        const balance = await v4Service.getAccountsBalances({
          currency_id: currencyId,
          wallet_id: walletId || undefined,
        });

        setUser(user);
        setPaymentsLimits(paymentsLimits.length > 0 ? paymentsLimits[0] : initialPaymentLimits);
        setBalance(balance.length > 0 ? balance[0] : initialBalance);
      } catch (error) {
        console.error("Error fetching widget data:", error);
        setWidgetDataError(String(error));
      } finally {
        setWidgetDataLoading(false);
      }
    };

    fetchWidgetData();
  }, [currencyId, walletId]);

  const widgetData = {
    user,
    paymentsLimits,
    balance,
    widgetDataLoading,
    widgetDataError,
  };

  return <DepositWithdrawContext.Provider value={widgetData}>{children}</DepositWithdrawContext.Provider>;
};

export const useDepositWithdrawContext = (): DepositWithdrawContextType => {
  const context = useContext(DepositWithdrawContext);

  if (context === undefined) {
    throw new Error("useDepositWithdrawContext must be used within a DepositWithdrawProvider");
  }

  return context;
};
